import { useState, useCallback } from "react";
import { NodeViewWrapper, type NodeViewProps } from "@tiptap/react";
import { BlockEditModal } from "../BlockEditModal";

export function PhotoNodeView({ node, updateAttributes, selected, deleteNode }: NodeViewProps) {
  const src = (node.attrs.src as string) || "";
  const alt = (node.attrs.alt as string) || "";
  const shape = (node.attrs.shape as string) || "square";
  const size = (node.attrs.size as number) ?? 64;
  const position = (node.attrs.position as string) || "right";

  const [isOpen, setIsOpen] = useState(false);
  const [draftSrc, setDraftSrc] = useState(src);
  const [draftAlt, setDraftAlt] = useState(alt);
  const [draftShape, setDraftShape] = useState(shape);
  const [draftSize, setDraftSize] = useState(size);
  const [draftPosition, setDraftPosition] = useState(position);

  const handleOpen = useCallback(() => {
    setDraftSrc(src); setDraftAlt(alt); setDraftShape(shape);
    setDraftSize(size); setDraftPosition(position);
    setIsOpen(true);
  }, [src, alt, shape, size, position]);

  const handleSave = useCallback(() => {
    updateAttributes({
      src: draftSrc, alt: draftAlt, shape: draftShape,
      size: draftSize, position: draftPosition,
    });
    setIsOpen(false);
  }, [draftSrc, draftAlt, draftShape, draftSize, draftPosition, updateAttributes]);

  const align = position === "left" ? "flex-start" : position === "center" ? "center" : "flex-end";

  return (
    <NodeViewWrapper data-type="photo-block" data-block-id={node.attrs.blockId} className={selected ? "ProseMirror-selectednode" : ""}>
      <div onClick={handleOpen} style={{
        display: "flex", justifyContent: align, padding: "8px 0", cursor: "pointer",
      }} title="Click to edit photo">
        {src ? (
          <img
            src={src}
            alt={alt}
            style={{
              height: size, width: size,
              objectFit: "cover",
              borderRadius: shape === "round" ? "50%" : 4,
              border: "1px solid var(--lilia-border, #e5e5e5)",
            }}
          />
        ) : (
          <div style={{
            height: size, width: size,
            display: "flex", alignItems: "center", justifyContent: "center",
            border: "1px dashed var(--lilia-border, #ccc)",
            borderRadius: shape === "round" ? "50%" : 4,
            color: "var(--lilia-text-muted, #999)",
            fontSize: 11, textAlign: "center",
          }}>[photo]</div>
        )}
      </div>

      <BlockEditModal
        sourceNode={node}
        title="Edit Photo"
        open={isOpen}
        onClose={() => setIsOpen(false)}
        onSave={handleSave}
        onDelete={() => { setIsOpen(false); deleteNode(); }}
        width={480}
      >
        <div style={{ display: "grid", gap: 12 }}>
          <Field label="Image URL" value={draftSrc} onChange={setDraftSrc} placeholder="https://… or /uploads/…" autoFocus />
          <Field label="Alt text" value={draftAlt} onChange={setDraftAlt} placeholder="Description for accessibility" />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            <Select label="Shape" value={draftShape} onChange={setDraftShape} options={[["square", "Square"], ["round", "Round"]]} />
            <NumberField label="Size (pt)" value={draftSize} onChange={setDraftSize} min={32} max={256} />
            <Select label="Position" value={draftPosition} onChange={setDraftPosition} options={[["left", "Left"], ["center", "Center"], ["right", "Right"]]} />
          </div>
        </div>
      </BlockEditModal>
    </NodeViewWrapper>
  );
}

function Field({ label, value, onChange, placeholder, autoFocus = false }: {
  label: string; value: string; onChange: (v: string) => void;
  placeholder?: string; autoFocus?: boolean;
}) {
  return (
    <div>
      <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{label}</label>
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} autoFocus={autoFocus}
        style={{ width: "100%", padding: "8px 10px", fontSize: 13, border: "1px solid var(--lilia-border, #ddd)", borderRadius: 4 }} />
    </div>
  );
}

function NumberField({ label, value, onChange, min, max }: {
  label: string; value: number; onChange: (v: number) => void; min: number; max: number;
}) {
  return (
    <div>
      <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{label}</label>
      <input type="number" value={value} onChange={(e) => onChange(Number(e.target.value) || min)} min={min} max={max}
        style={{ width: "100%", padding: "8px 10px", fontSize: 13, border: "1px solid var(--lilia-border, #ddd)", borderRadius: 4 }} />
    </div>
  );
}

function Select({ label, value, onChange, options }: {
  label: string; value: string; onChange: (v: string) => void; options: Array<[string, string]>;
}) {
  return (
    <div>
      <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        style={{ width: "100%", padding: "8px 10px", fontSize: 13, border: "1px solid var(--lilia-border, #ddd)", borderRadius: 4 }}>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}
